import { z } from 'zod';
import { objectIdSchema } from './objectId';
import { McmSchema } from './mcm';
import { AudienceSchema } from './audience';
import { WithIdSchema } from './utils';
import { BrandVenueTypeSchema } from './brand';
import { PropertySchema } from './property';
import { VenueHoursSchema } from './venueHours';
import { VenueLocationLong, VenueLocationLat } from './const';

export const DayTypeSchema = z.enum(['embark', 'sea', 'port', 'debark']);

export const DayTypeBookingHoursSchema = z.object({
  dayType: DayTypeSchema,
  hours: z.array(VenueHoursSchema),
});

export const VenueConfigurationSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  occupancy: z.number(),
  images: z.string().array(),
});

export const NewVenueTemplateSchema = z.object({
  name: z.string(),
  description: z.string().optional(),
  images: z.string().array(),
  subvenues: z.string().array().optional(),
  occupancy: z.number(),
  types: objectIdSchema.array(),
  level: z.number().nullable().optional(),
  configurations: VenueConfigurationSchema.array(),
  order: z.number(),
  bookingHours: z.array(DayTypeBookingHoursSchema).optional(),
  locationLong: z.enum(VenueLocationLong).optional(),
  locationLat: z.enum(VenueLocationLat).optional(),
  property: objectIdSchema,
  isPrivate: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  hideFromSignage: z.boolean().optional(),
  attributeFiles: z
    .object({
      location: z.string(),
      name: z.string(),
    })
    .array()
    .optional(),
  archived: z.boolean().optional(),
  mcm: McmSchema.optional(),
  audience: AudienceSchema.optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const BareVenueTemplateSchema = NewVenueTemplateSchema.merge(WithIdSchema);

export const VenueTemplateSchema = z.object({
  _id: objectIdSchema,
  name: z.string(),
  description: z.string().optional(),
  images: z.string().array(),
  subvenues: z.string().array().optional(),
  occupancy: z.number(),
  types: z.array(BrandVenueTypeSchema),
  level: z.number(),
  configurations: VenueConfigurationSchema.array(),
  order: z.number(),
  bookingHours: z.array(DayTypeBookingHoursSchema),
  locationLong: z.enum(VenueLocationLong).optional(),
  locationLat: z.enum(VenueLocationLat).optional(),
  property: PropertySchema,
  isPrivate: z.boolean().optional(),
  isHidden: z.boolean().optional(),
  hideFromSignage: z.boolean().optional(),
  attributeFiles: z
    .object({
      location: z.string(),
      name: z.string(),
    })
    .array()
    .optional(),
  archived: z.boolean().optional(),
  mcm: McmSchema.optional(),
  audience: AudienceSchema.optional(),
  createdAt: z.date().optional(),
  updatedAt: z.date().optional(),
});

export const SimpleVenueTemplateSchema = BareVenueTemplateSchema.pick({
  _id: true,
  name: true,
  property: true,
  archived: true,
});

export const VenueOrderSchema = z.object({
  id: objectIdSchema,
  name: z.string(),
  order: z.number(),
  archived: z.boolean().optional(),
});

export const VenueTemplateSetArchivedSchema = z.object({
  venueTemplateId: objectIdSchema,
  archived: z.boolean(),
});

export type BareVenueTemplate = z.infer<typeof BareVenueTemplateSchema>;
